import axios from 'axios';
import store from '@/store';

/**
 * Service pour gérer toutes les opérations liées aux catégories
 * - Récupération des catégories depuis l'API
 * - Mise en cache des catégories
 * - Gestion des préférences de catégories
 */
class CategoryService {
  /**
   * Récupère toutes les catégories depuis l'API
   * @param {Object} options - Options de récupération
   * @param {Boolean} options.showInNav - Filtre pour afficher uniquement les catégories qui doivent apparaître dans la navigation
   * @param {String} options.sort - Champ de tri (nom, priorité, articleCount)
   * @returns {Promise<Array>} Liste des catégories
   */
  async getCategories(options = {}) {
    try {
      const { showInNav, sort = 'priority' } = options;
      
      let queryParams = `?sort=${sort}`;
      if (showInNav !== undefined) {
        queryParams += `&showInNav=${showInNav}`;
      }
      
      const response = await axios.get(`/api/categories${queryParams}`);
      return response.data.categories;
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      return [];
    }
  }

  /**
   * Récupère une catégorie par son slug
   * @param {String} slug - Le slug de la catégorie
   * @returns {Promise<Object>} La catégorie
   */
  async getCategoryBySlug(slug) {
    try {
      const response = await axios.get(`/api/categories/${slug}`);
      return response.data.category;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la catégorie ${slug}:`, error);
      return null;
    }
  }

  /**
   * Récupère les articles d'une catégorie
   * @param {String} slug - Le slug de la catégorie
   * @param {Object} options - Options de pagination et de filtrage
   * @returns {Promise<Object>} Les articles et les métadonnées
   */
  async getCategoryArticles(slug, options = {}) {
    try {
      const { page = 1, limit = 20, sort = 'date', filter } = options;
      
      let queryParams = `?page=${page}&limit=${limit}&sort=${sort}`;
      if (filter) {
        queryParams += `&filter=${filter}`;
      }
      
      const response = await axios.get(`/api/categories/${slug}/articles${queryParams}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des articles pour ${slug}:`, error);
      return { articles: [], total: 0, page: 1, totalPages: 1 };
    }
  }

  /**
   * Met à jour les préférences de catégories de l'utilisateur
   * @param {Array} categoryIds - Liste des IDs de catégories préférées
   * @returns {Promise<Boolean>} Succès de l'opération
   */
  async updatePreferredCategories(categoryIds) {
    try {
      const response = await axios.post('/api/users/preferences/categories', { categoryIds });
      return response.data.success;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des préférences de catégories:', error);
      return false;
    }
  }
  
}

// Créer une instance unique du service à utiliser dans toute l'application
const categoryService = new CategoryService();

export default categoryService;